// #region imports

// --- Angular
import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';

// --- RxJs
import { BehaviorSubject, Subscription, combineLatest } from 'rxjs';

// --- 3rd Party
import { TranslateService } from '@ngx-translate/core';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';

// --- HS Code
import { Constants, GroupModel, ManageRespondentDropdownStates, RespondentModel, RespondentStatuses } from '../../../models';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { RespondentOptionsBottomSheetComponent } from '../../../shared';
import { RespondentOptionsBottomSheetInput, RespondentOptionsBottomSheetOutput } from '../../../shared/respondent-options-bottom-sheet/respondent-options-bottom-sheet.model';
import { OidcService } from 'src/app/core/services';

// #endregion

@Component({
  selector: 'hs-manage-respondents-respondent-v2',
  templateUrl: './manage-respondents-respondent-v2.component.html',
  styleUrls: ['./manage-respondents-respondent-v2.component.scss']
})
export class ManageRespondentsRespondentV2Component implements OnChanges, OnDestroy {

  @Input() availableGroups: GroupModel[] | null = null;
  @Input() group: GroupModel | null = null;
  @Input() groups: GroupModel[] | null = null;
  @Input() numberOfRespondentsPerGroup: number = Constants.NUMBER_OF_RESPONDENTS_PER_GROUP;
  @Input() respondentIndex: number = 0;
  @Input() respondents: RespondentModel[] | null = null;
  @Input() showWarning: boolean = false;
  @Input() isMobile: boolean | null = false;
  @Input() isMobileUser: boolean = false;

  @Output() addRespondent = new EventEmitter<{}>();
  @Output() deleteRespondent = new EventEmitter<RespondentModel>();
  @Output() moveRespondent = new EventEmitter<{}>();
  @Output() remindRespondentClick = new EventEmitter<RespondentModel>();

  private _canMoveSubscription: Subscription = Subscription.EMPTY;
  private _respondentSubject$: BehaviorSubject<RespondentModel | null> = new BehaviorSubject<RespondentModel | null>(null);
  private _availableGroupsSubject$: BehaviorSubject<GroupModel[] | null> = new BehaviorSubject<GroupModel[] | null>(null);
  private _groupsSubject$: BehaviorSubject<GroupModel[] | null> = new BehaviorSubject<GroupModel[] | null>(null);

  canMoveRespondent: boolean = false;
  canDeleteRespondent: boolean = false;
  hasDestinations: boolean = false;
  manageRespondentDropdownStates = ManageRespondentDropdownStates;
  moveAlert: string = '';
  respondent: RespondentModel = {} as RespondentModel;
  respondentDropdownState = ManageRespondentDropdownStates.initial;
  respondentStatuses = RespondentStatuses;
  validMoveDestinations: GroupModel[] = new Array<GroupModel>();

  moveDestinations: any[] = [];

  private _bottomSheetSubscriptions: Subscription[] = [];

  constructor(
    private _translateService: TranslateService,
    private _bottomSheet: MatBottomSheet,
    private _oidcService: OidcService,
  ) {
    this._canMoveSubscription = combineLatest(
      { respondent: this._respondentSubject$, availableGroups: this._availableGroupsSubject$, groups: this._groupsSubject$ }
    ).subscribe(({ respondent, availableGroups, groups}) => {
      this.canMoveRespondent = false;

      if (!respondent) return;
      if (!availableGroups) return;
      if (!groups) return;

      this.validMoveDestinations = groups.filter(ag => ag.id.toLowerCase() !== respondent.groupId.toLowerCase());
      this.hasDestinations = this.validMoveDestinations.length > 0;

      this.canDeleteRespondent = ((respondent.status < RespondentStatuses.started)||(respondent.status===RespondentStatuses.declined));
      
      if (!this._oidcService.isCurrentlyImpersonate()) //Only refused when NOT an impersonation      
        if (respondent.status >= RespondentStatuses.started) return; //then we can't move started users

      this.moveDestinations = this.validMoveDestinations.map(vmd => {
        return {
          isFull: (vmd.respondents.filter(r => r.isSet).length >= this.numberOfRespondentsPerGroup),
          groupName: vmd.groupName,
          id: vmd.id
        }
      });
      this.canMoveRespondent = this.validMoveDestinations.length > 0;
      
    });

  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes['respondents'] && changes['respondents'].currentValue) {
      if (this.respondents && this.respondentIndex) {
        const indexedRespondent = this.respondents.find((r: RespondentModel) => {
          return r.groupIndex === this.respondentIndex;
        });
        if (indexedRespondent) {
          this.respondent = indexedRespondent;
          this._respondentSubject$.next(this.respondent)
        }
      }
    }

    if (changes && changes['groups']) {
      if (this.groups) this._groupsSubject$.next(this.groups);
    }

    if (changes && changes['availableGroups']) {
      if (this.availableGroups) this._availableGroupsSubject$.next(this.availableGroups);
    }
  }

  ngOnDestroy(): void {
    if (this._canMoveSubscription !== Subscription.EMPTY) {
      this._canMoveSubscription.unsubscribe();
      this._canMoveSubscription = Subscription.EMPTY;
    }

    this._bottomSheetSubscriptions.forEach((sub) => sub.unsubscribe());
    this._bottomSheetSubscriptions = [];
  }

  onAddClicked(): void {
    if (!this.group) return;

    this.addRespondent.emit({ group: this.group, respondentIndex: this.respondentIndex, respondent: this.respondent });
  }

  updateManageRespondentDropdownState(state: number): void {
    if (!this.respondent) return;

    if (this.respondent.status >= RespondentStatuses.started) {
      this.moveAlert = this._translateService.instant('inviteRespondentsV2MoveRespondentErrorRespondentStarted');
      return;
    }

    this.moveAlert = '';
    this.respondentDropdownState = state;
  }

  /* Return true if action is succesful which clears the menu;
     false to display the alert message and leave the menu open*/
  onDeleteClicked(): boolean {
    if (!this.respondent) return true;
    if ((this.respondent.status >= RespondentStatuses.started)&&(this.respondent.status!=RespondentStatuses.declined)) {
      this.moveAlert = this._translateService.instant('inviteRespondentsV2DeleteRespondentErrorRespondentStarted');
      return false;
    };
    this.deleteRespondent.emit(this.respondent);
    return true;
  }

  onMoveRespondentClick(
    respondent: RespondentModel,
    targetGroup: any,
    manageRespondentDropdown?: NgbDropdown
  ): boolean {
    if (!this.groups) return false;
    if (respondent.status >= RespondentStatuses.started) {
      return false;
    }

    if (!!targetGroup['isFull'] === true) {
      this.moveAlert = this._translateService.instant(
        'inviteRespondentsV2MoveRespondentErrorgroupFull',
        {groupName: targetGroup['groupName'], firstName: respondent.firstName, lastName: respondent.lastName});
      return false;
    }

    const destination: GroupModel | undefined = this.groups.find(g => g.id.toLowerCase() === targetGroup['id']);
    if (!destination) return false;
    this.moveRespondent.emit({ respondent, targetGroup: destination });

    if (!!manageRespondentDropdown) {
      manageRespondentDropdown.close();
    }
    return true;
  }

  onOpenRespondentOptions(
    respondent: RespondentModel,
    respondentDropdownState: ManageRespondentDropdownStates,
    manageRespondentDropdownStates: typeof ManageRespondentDropdownStates,
    canMoveRespondent: boolean,
    canDeleteRespondent: boolean,
    moveDestinations: any[],
    moveAlert: string,
  ) {

    this._bottomSheetSubscriptions.forEach((sub) => sub.unsubscribe());
    this._bottomSheetSubscriptions = [];

    const bottomSheetRef = this._bottomSheet
      .open<
      RespondentOptionsBottomSheetComponent,
      RespondentOptionsBottomSheetInput,
      RespondentOptionsBottomSheetOutput
    >(RespondentOptionsBottomSheetComponent, {
     data: {
      respondent,
      respondentDropdownState,
      manageRespondentDropdownStates,
      canMoveRespondent,
      canDeleteRespondent,
      moveAlert,
      moveDestinations,
      buttons: ['move', 'delete'],
      isMobileUser: this.isMobileUser,
      canResendEmail: this.respondent.canResendEmail
    },
      panelClass: 'custom-bottom-sheet',
    });

    this._bottomSheetSubscriptions.push(bottomSheetRef.instance.deleteClicked.subscribe(() => {
      if (this.onDeleteClicked())
        bottomSheetRef.instance.closeIconClick();
      else bottomSheetRef.instance.data.moveAlert=this.moveAlert;
    }));



    this._bottomSheetSubscriptions.push(bottomSheetRef.instance.updateManageRespondentDropdownState.subscribe((state) => {
      this.updateManageRespondentDropdownState(state);
      bottomSheetRef.instance.data.moveAlert = this.moveAlert;
      bottomSheetRef.instance.data.respondentDropdownState = this.respondentDropdownState;
    }));

    this._bottomSheetSubscriptions.push(bottomSheetRef.instance.moveRespondentClick.subscribe(({respondent, targetGroup}) => {
      const result = this.onMoveRespondentClick(respondent, targetGroup);
      if (result) {
        this._bottomSheet.dismiss();
        return;
      }
      bottomSheetRef.instance.data.moveAlert = this.moveAlert;
    }));

    this._bottomSheetSubscriptions.push(bottomSheetRef.afterDismissed().subscribe(()=> {
      this.respondentDropdownState = this.manageRespondentDropdownStates.initial;
    }));

    this._bottomSheetSubscriptions.push(bottomSheetRef.instance.remindRespondentClick.subscribe((respondent) => {
      this.remindRespondentClick.emit(respondent);
      this._bottomSheet.dismiss();
    }));
  }

  onDropdownChanged(isDropDownOpen: boolean) {
    if (!isDropDownOpen) {
      this.moveAlert = '';
    } else {
      this.respondentDropdownState = ManageRespondentDropdownStates.initial;
    }
  }

  onSendReminderClick(respondent: RespondentModel) {
    respondent.canResendEmail = false;
    const now = new Date();
    respondent.lastEmailSentDate = new Date(
      Date.UTC(
        now.getFullYear(),
        now.getMonth() + 1,
        now.getDay(),
        now.getHours(),
        now.getMinutes()
      )
    ).toString();
    this.remindRespondentClick.emit(respondent);
  }

}
